import * as PIXI from 'pixi.js';
import type { AssetLoadProgress } from '@/managers/AssetManager';

// Use a more pixelated font that matches game's dark, spooky aesthetics
const fontFamily = `"VT323", "Courier New", monospace, serif`;

// Spooky sepia color palette based on the game's visual style
const COLORS = {
  BACKGROUND: 0x000000,
  BACKGROUND_ALPHA: 0.92,
  TEXT: 0xd0c097, // Faded parchment color
  PROGRESS_BG: 0x242018, // Dark sepia
  PROGRESS_BORDER: 0x393428, // Slightly lighter sepia
  PROGRESS_FILL: 0x8f5922, // Aged brown
  PROGRESS_TEXT: 0x9c8e6b, // Faded gold
  SPINNER: 0xc2a978 // Light sepia
};

export class SplashScreen {
  private container: PIXI.Container;
  private background: PIXI.Graphics;
  private isVisible: boolean = true;

  // Loading UI elements
  private loadingContainer: PIXI.Container;
  private loadingText: PIXI.Text;

  // Progress bar elements
  private progressBar: PIXI.Graphics;
  private progressBarBg: PIXI.Graphics;
  private progressText: PIXI.Text;
  private isShowingProgress: boolean = false;

  constructor(screenWidth: number, screenHeight: number) {
    this.container = new PIXI.Container();
    this.container.sortableChildren = true;

    // Create dark vignette background
    this.background = new PIXI.Graphics();
    
    // Main dark background
    this.background.beginFill(COLORS.BACKGROUND, COLORS.BACKGROUND_ALPHA);
    this.background.drawRect(0, 0, screenWidth, screenHeight);
    this.background.endFill();
    
    // Add spooky noise pattern
    const noiseTexture = this.createNoiseTexture(screenWidth, screenHeight);
    const noiseSprite = new PIXI.Sprite(noiseTexture);
    noiseSprite.alpha = 0.18; // Subtle noise
    this.background.addChild(noiseSprite);
    
    this.container.addChild(this.background);

    // Create loading UI elements
    this.loadingContainer = new PIXI.Container();

    // Create eerie loading text
    this.loadingText = new PIXI.Text('LOADING...', {
      fontFamily,
      fontSize: 22,
      fill: COLORS.TEXT,
      align: 'center',
      letterSpacing: 3,
      padding: 4,
      dropShadow: true,
      dropShadowColor: 0x000000,
      dropShadowDistance: 3,
      dropShadowAlpha: 0.5,
    });
    this.loadingText.resolution = window.devicePixelRatio || 2;
    this.loadingText.anchor.set(0.5);
    this.loadingText.position.set(0, -20);
    this.loadingContainer.addChild(this.loadingText);

    // Create a spooky, aged-looking progress bar background
    this.progressBarBg = new PIXI.Graphics();
    
    // Outer border with uneven edges
    const outerBorder = new PIXI.Graphics();
    outerBorder.beginFill(COLORS.PROGRESS_BORDER);
    
    // Create uneven border for an old, worn look
    const borderPoints = [];
    const barWidth = 300;
    const barHeight = 20;
    const jitter = 3; // Amount of edge randomness
    
    // Top edge with jitter
    for (let x = -barWidth/2; x <= barWidth/2; x += 4) {
      const y = -barHeight/2 - (Math.random() * jitter);
      borderPoints.push(new PIXI.Point(x, y));
    }
    
    // Right edge
    for (let y = -barHeight/2; y <= barHeight/2; y += 4) {
      const x = barWidth/2 + (Math.random() * jitter);
      borderPoints.push(new PIXI.Point(x, y));
    }
    
    // Bottom edge with jitter
    for (let x = barWidth/2; x >= -barWidth/2; x -= 4) {
      const y = barHeight/2 + (Math.random() * jitter);
      borderPoints.push(new PIXI.Point(x, y));
    }
    
    // Left edge
    for (let y = barHeight/2; y >= -barHeight/2; y -= 4) {
      const x = -barWidth/2 - (Math.random() * jitter);
      borderPoints.push(new PIXI.Point(x, y));
    }
    
    outerBorder.drawPolygon(borderPoints);
    outerBorder.endFill();
    
    // Inner background
    this.progressBarBg.beginFill(COLORS.PROGRESS_BG);
    this.progressBarBg.drawRect(-barWidth/2 + 4, -barHeight/2 + 4, barWidth - 8, barHeight - 8);
    this.progressBarBg.endFill();
    
    // Position the bar
    this.progressBarBg.y = 15;
    
    outerBorder.y = 15;
    
    this.loadingContainer.addChild(outerBorder);
    this.loadingContainer.addChild(this.progressBarBg);

    // Create spooky progress bar fill
    this.progressBar = new PIXI.Graphics();
    this.progressBar.x = -146; // Adjusted for the new progress bar design
    this.progressBar.y = 11;
    this.loadingContainer.addChild(this.progressBar);

    // Create spooky progress percentage text
    this.progressText = new PIXI.Text('0%', {
      fontFamily,
      fontSize: 18,
      fill: COLORS.PROGRESS_TEXT,
      align: 'center',
      letterSpacing: 1,
      padding: 4,
      dropShadow: true,
      dropShadowColor: 0x000000,
      dropShadowDistance: 2,
      dropShadowAlpha: 0.7,
    });
    this.progressText.resolution = window.devicePixelRatio || 2;
    this.progressText.anchor.set(0.5);
    this.progressText.position.set(0, 35);
    this.loadingContainer.addChild(this.progressText);

    // Add subtle flickering candle glow effect around the loading elements
    const glowEffect = new PIXI.Graphics();
    glowEffect.beginFill(COLORS.PROGRESS_FILL, 0.1);
    glowEffect.drawCircle(0, 15, 150);
    glowEffect.endFill();
    glowEffect.alpha = 0;
    this.loadingContainer.addChildAt(glowEffect, 0);
    
    // Animate the glow with flickering effect
    let glowIntensity = 0;
    const animateGlow = () => {
      glowIntensity = 0.05 + Math.random() * 0.1;
      glowEffect.alpha = glowIntensity;
      requestAnimationFrame(animateGlow);
    };
    animateGlow();

    // Position the loading container in the center
    this.loadingContainer.position.set(screenWidth / 2, screenHeight * 0.5);
    this.loadingContainer.visible = true; // Show by default
    this.container.addChild(this.loadingContainer);

    // Set z-index to ensure splash screen is on top
    this.container.zIndex = 1000;
  }

  public getContainer(): PIXI.Container {
    return this.container;
  }

  public show(): void {
    this.isVisible = true;
    this.container.visible = true;
  }

  public hide(): void {
    this.isVisible = false;
    this.container.visible = false;
  }

  public isShowing(): boolean {
    return this.isVisible;
  }

  public onStart(callback: () => void): void {
    // Automatically start without waiting for button click
    // Schedule a small delay to let the UI render first
    setTimeout(() => {
      callback();
    }, 100);
  }

  public updateScreenSize(width: number, height: number): void {
    // Update background size
    this.background.clear();
    this.background.beginFill(0x000000, 0.75);
    this.background.drawRect(0, 0, width, height);
    this.background.endFill();

    // Update loading container position
    this.loadingContainer.position.set(width / 2, height * 0.5);
  }

  /**
   * Show loading UI with progress bar
   */
  public showLoading(): void {
    this.isLoading = true;
    this.isShowingProgress = true;
    this.loadingContainer.visible = true;
  }

  /**
   * Update loading progress with progress bar
   */
  public updateProgress(progress: AssetLoadProgress): void {
    if (!this.isShowingProgress) return;

    // Update progress bar with a single solid color
    this.progressBar.clear();
    
    const width = (progress.percentage / 100) * (300 - 16);
    const height = 12;
    
    // Create a simple solid fill
    this.progressBar.beginFill(COLORS.PROGRESS_FILL);
    this.progressBar.drawRect(0, 0, width, height);
    this.progressBar.endFill();

    // Update progress text
    this.progressText.text = `${progress.percentage}%`;

    // Update loading text based on stage (without file names/count)
    let stageText = '';
    switch (progress.stage) {
      case 'images':
        stageText = 'Loading Images...';
        break;
      case 'audio':
        stageText = 'Loading Audio...';
        break;
      case 'complete':
        stageText = 'Loading Complete!';
        break;
      default:
        stageText = 'Loading...';
    }
    
    this.loadingText.text = stageText;
  }

  /**
   * Update the loading screen (for animation)
   */
  public update(): void {
    if (this.isShowingProgress) {
      // Spinner animation is handled by animateSpinner()
    }
  }

  /**
   * Hide the loading UI
   */
  public hideLoading(): void {
    this.isLoading = false;
    this.loadingContainer.visible = false;

    // No animations to cancel now that we've removed the spinner
  }

  /**
   * Create the spinner graphic
   */
  /**
   * Creates a noisy texture for the spooky background
   */
  private createNoiseTexture(width: number, height: number): PIXI.Texture {
    // Create a canvas to draw the noise
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    
    if (ctx) {
      // Fill with a dark color
      ctx.fillStyle = '#000000';
      ctx.fillRect(0, 0, width, height);
      
      // Draw noise pixels
      const imageData = ctx.getImageData(0, 0, width, height);
      const data = imageData.data;
      
      for (let i = 0; i < data.length; i += 4) {
        // Only add noise to some pixels for a sparser effect
        if (Math.random() < 0.15) {
          // Sepia tones
          const shade = Math.floor(Math.random() * 40) + 10;
          data[i] = shade + 10; // R - slightly more red for sepia
          data[i + 1] = shade + 5; // G
          data[i + 2] = shade; // B - less blue for sepia
          data[i + 3] = 100; // Alpha
        }
      }
      
      ctx.putImageData(imageData, 0, 0);
    }
    
    return PIXI.Texture.from(canvas);
  }

  /**
   * Update loading text
   */
  public updateLoadingText(text: string): void {
    this.loadingText.text = text;
  }

  /**
   * Previously showed the start button (now a no-op since we removed the button)
   */
  public showStartButton(): void {
    // Button no longer exists, this is a no-op
  }
}
